import PromiseRequest from "./PromiseRequest";
import {
    getServiceDefinition,
    OptionalParams,
    TServiceName,
    TServiceParameter,
    TServiceParameters
} from "../constants/ApiServices";
import {_} from 'lodash';
import {_d, isNullOrUndefined} from "../util/misc";
import {global} from "../appContext";
import {storeObservable} from "../util/sessionstore";
import pubsub from "../pubsub";

interface IAbortIfMissingTokenOptions {
    errorHandler?: (...args: any[]) => void;
}

export namespace nsRequest {

    /**
     * When a mandatory token is missing from prepared params, trigger not_authenticated (unless suppressed)
     * and return true so the caller aborts before sending the request.
     */
    export function abortIfMissingMandatoryToken(
        serviceName: TServiceName,
        preparedParams: TServiceParameters,
        options?: IAbortIfMissingTokenOptions
    ): boolean {
        const paramKeys = getServiceDefinition(serviceName)?.paramKeys;
        if (!paramKeys?.includes('token') || OptionalParams[serviceName]?.includes('token')) {
            return false;
        }
        const token = preparedParams['token'];
        if (!isNullOrUndefined(token) && token !== '') {
            return false;
        }
        if (!options?.errorHandler) {
            pubsub.trigger("not_authenticated");
        }
        return true;
    }

    export function getPromiseRequest(serviceName: TServiceName): PromiseRequest {
        return new PromiseRequest(serviceName);
    }

    export function getParams(serviceName: TServiceName, ...params: TServiceParameters[]): TServiceParameters {
        if (!serviceName) {
            _d.error('No service name', serviceName)
        }
        const ret: TServiceParameters = {};
        const src: any = _.cloneDeep(_.defaultsDeep(params[0], ...params.slice(1)));
        const paramKeys = getServiceDefinition(serviceName).paramKeys;
        // fill inn any missing parameters
        paramKeys.forEach(key => {
            let val: any = src[key];
            if (key === 'orgid' && isNullOrUndefined(val) && !isNullOrUndefined(src['orgnr'])) {
                val = src['orgnr'];
            }
            if (isNullOrUndefined(val)) {
                val = getParam(key, serviceName);
                if (val === undefined) {
                    const isParameterOptional = OptionalParams[serviceName]?.includes(key);
                    if (isParameterOptional) {
                        ret[key] = ''
                        return
                    }
                    _d.warn(`Could not resolve parameter for ${key}`, serviceName);
                }
            }
            val = formatParam(key, val, serviceName);
            ret[key] = val ?? null;
        });
        return ret;
    }

    function getParam(key: TServiceParameter, serviceName: TServiceName): TServiceParameters[TServiceParameter] {
        switch (key) {
            case 'asc':
                return true;
            case 'deleted':
            case 'manualobjects':
            case 'onlyids':
            case 'preview':
            case 'skipsystemavailableto':
                return false;
            case 'orgid':
                return global.login.selectedOrganizationId();
            case 'orgnr':
                switch (serviceName) {
                    case 'createcompany':
                    case 'createsyncfield':
                    case 'createsyncfielddisplayname':
                    case 'deletesyncfield':
                    case 'deletesyncfielddisplayname':
                    case 'getsyncfields':
                    case 'updatesyncfield':
                    case 'updatesyncfielddisplayname': {
                        return global.login.getHomeOrg()?.organizationid
                    }
                    default:
                        return global.login.selectedOrganizationId();
                }
            case 'importfileid':
            case 'pagenr':
            case 'syncstep':
                return 0;
            case 'jruleset':
            case 'searchcols':
            case 'searchkey':
            case 'sortcol':
                return '';
            case 'resultsprpage':
                return storeObservable('num.search.results')();
            case 'targetid':
                if (serviceName == 'searchsystem') {
                    return global.login.user()?.id
                }
                break
            case 'token':
                return  global.login.getToken();
        }
    }

    function formatParam(key: TServiceParameter, val: any, serviceName: TServiceName): any {
        switch (key) {
            case 'id': {
                switch (serviceName) {
                    case 'deletecompany':
                        return val
                    default:
                        return str2int(val);
                }
            }
            case 'importfileid':
            case 'importformatid':
            case 'languageid':
            case 'messageid':
            case 'nr':
            case 'objectid':
            case 'objecttypeid':
            case 'rulesetid':
            case 'syncstepid':
            case 'targetid':
            case 'targetobjectid':
                return str2int(val);
            case 'preview':
                return int2Bool(val);
            case 'searchkey':
                // bugfix: the searchruleset API doesn't behave correctly when searchkey is formatted
                if (serviceName == 'searchruleset') {
                    return val
                }
                // escape brackets to conform to SQL
                return val.replace(/(\[)|(])/gm, (match: string, open?: string, close?: string) => {
                    if (open) {
                        return '[[]';
                    }
                    if (close) {
                        // no need to escape
                        return ']';
                    }
                });
            default:
                return val;
        }
    }

    function int2Bool(val: number | boolean): boolean {
        return typeof val === 'boolean' ? val : Boolean(Number(val));
    }

    function str2int(val: string | number): number {
        if (_.isInteger(val))
            return val as number;
        else
            return parseInt(val as string);
    }

}
